import Vue from 'vue'
import Vuex from 'vuex'
import { favoritesAPI, betAPI, rankingAPI, followsAPI } from '@/api/index'

Vue.use(Vuex)

// Generate or load persistent UUID
function getOrCreateUUID() {
  const KEY = 'soccer_uuid'
  let uuid = localStorage.getItem(KEY)
  if (!uuid) {
    uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
    localStorage.setItem(KEY, uuid)
  }
  return uuid
}

export default new Vuex.Store({
  state: {
    uuid: getOrCreateUUID(),
    // Set of analysis_id strings the user has favorited
    favoritedIds: new Set(),
    // Whether we are viewing favorites mode
    favoritesMode: false,
    // Loaded favorites list for display
    favoriteMatches: [],
    favoritesPage: 1,
    favoritesHasMore: false,
    favoritesLoading: false,
    // Popular mode
    popularPage: 1,
    popularHasMore: false,
    popularLoading: false,
    popularMode: false,
    popularMatches: [],
    // Bets mode
    betsMode: false,
    betMatches: [],
    betStats: { win_amount: 0, lose_amount: 0, net_amount: 0 },
    betsPage: 1,
    betsHasMore: false,
    betsLoading: false,
    betsLoading: false,
    betMatchIds: {}, // { analysis_id: true }
    // Ranking mode
    rankingMode: false,
    rankingData: [],
    rankingLoading: false,
    // Follows mode
    followsMode: false,
    followsData: [],
    followsLoading: false
  },

  getters: {
    uuid: state => state.uuid,
    isFavorited: state => analysisId => state.favoritedIds.has(Number(analysisId)),
    favoritesMode: state => state.favoritesMode,
    favoriteMatches: state => state.favoriteMatches,
    favoritesHasMore: state => state.favoritesHasMore,
    favoritesLoading: state => state.favoritesLoading,
    popularMatches: state => state.popularMatches,
    popularHasMore: state => state.popularHasMore,
    popularLoading: state => state.popularLoading,
    popularMode: state => state.popularMode,
    betsMode: state => state.betsMode,
    betMatches: state => state.betMatches,
    betStats: state => state.betStats,
    betsHasMore: state => state.betsHasMore,
    betsLoading: state => state.betsLoading,
    hasBet: state => (analysis_id) => !!state.betMatchIds[analysis_id],
    rankingMode: state => state.rankingMode,
    rankingData: state => state.rankingData,
    rankingLoading: state => state.rankingLoading,
    followsMode: state => state.followsMode,
    followsData: state => state.followsData,
    followsLoading: state => state.followsLoading
  },

  mutations: {
    SET_FAVORITED_IDS(state, ids) {
      state.favoritedIds = new Set(ids.map(Number))
    },
    ADD_FAVORITE_ID(state, id) {
      const newSet = new Set(state.favoritedIds)
      newSet.add(Number(id))
      state.favoritedIds = newSet
    },
    REMOVE_FAVORITE_ID(state, id) {
      const newSet = new Set(state.favoritedIds)
      newSet.delete(Number(id))
      state.favoritedIds = newSet
    },
    MERGE_FAVORITED_IDS(state, ids) {
      const newSet = new Set(state.favoritedIds)
      ids.forEach(id => newSet.add(Number(id)))
      state.favoritedIds = newSet
    },
    SET_FAVORITES_MODE(state, mode) {
      state.favoritesMode = mode
    },
    SET_FAVORITE_MATCHES(state, { matches, page, hasMore }) {
      if (page === 1) {
        state.favoriteMatches = matches
      } else {
        state.favoriteMatches = [...state.favoriteMatches, ...matches]
      }
      state.favoritesPage = page
      state.favoritesHasMore = hasMore
    },
    SET_FAVORITES_LOADING(state, val) {
      state.favoritesLoading = val
    },
    // Remove a match from the displayed favorites list (no full reload)
    REMOVE_MATCH_FROM_LIST(state, analysisId) {
      state.favoriteMatches = state.favoriteMatches.filter(
        m => m.analysis_id !== Number(analysisId)
      )
    },
    SET_POPULAR_MODE(state, mode) {
      state.popularMode = mode
    },
    SET_POPULAR_MATCHES(state, { matches, page, hasMore }) {
      if (page === 1) {
        state.popularMatches = matches
      } else {
        state.popularMatches = [...state.popularMatches, ...matches]
      }
      state.popularPage = page
      state.popularHasMore = hasMore
    },
    SET_POPULAR_LOADING(state, val) {
      state.popularLoading = val
    },
    SET_BETS_MODE(state, mode) {
      state.betsMode = mode
    },
    SET_BETS_DATA(state, { matches, stats, page, hasMore }) {
      if (page === 1) {
        state.betMatches = matches
      } else {
        state.betMatches = [...state.betMatches, ...matches]
      }
      if (stats) {
        state.betStats = stats
      }
      state.betsPage = page
      state.betsHasMore = hasMore
    },
    SET_BETS_LOADING(state, val) {
      state.betsLoading = val
    },
    SET_BET_MATCH_IDS(state, idsMap) {
      state.betMatchIds = { ...state.betMatchIds, ...idsMap }
    },
    MARK_MATCH_AS_BET(state, analysis_id) {
      Vue.set(state.betMatchIds, analysis_id, true)
    },
    SET_RANKING_MODE(state, mode) { state.rankingMode = mode },
    SET_RANKING_DATA(state, data) { state.rankingData = data },
    SET_RANKING_LOADING(state, val) { state.rankingLoading = val },
    SET_FOLLOWS_MODE(state, mode) { state.followsMode = mode },
    SET_FOLLOWS_DATA(state, data) { state.followsData = data },
    SET_FOLLOWS_LOADING(state, val) { state.followsLoading = val }
  },

  actions: {
    // Load the set of favorited IDs (lightweight - just pagination page 1 IDs)
    async loadFavoriteIds({ state, commit }) {
      try {
        // Load up to 500 to get all IDs (adjust if user can have more)
        const res = await favoritesAPI.getFavorites(state.uuid, 1, 500)
        const ids = (res.data.items || []).map(i => i.analysis_id)
        commit('SET_FAVORITED_IDS', ids)
      } catch (e) {
        console.warn('Failed to load favorite IDs', e)
      }
    },

    // Load the set of analysis_ids the user has bet on
    async loadBetIds({ state, commit }) {
      try {
        const res = await betAPI.getBets(state.uuid, 1, 500)
        const bets = res.data.items || []
        const idsMap = {}
        bets.forEach(b => {
          if (b.analysis_id) idsMap[b.analysis_id] = true
        })
        commit('SET_BET_MATCH_IDS', idsMap)
      } catch (e) {
        console.warn('Failed to load bet IDs', e)
      }
    },

    // Load favorites for display list
    async loadFavoriteMatches({ state, commit }, page = 1) {
      commit('SET_FAVORITES_LOADING', true)
      try {
        const res = await favoritesAPI.getFavorites(state.uuid, page, 30)
        commit('SET_FAVORITE_MATCHES', {
          matches: res.data.items || [],
          page,
          hasMore: res.data.has_more
        })
      } catch (e) {
        console.error('Failed to load favorites', e)
      } finally {
        commit('SET_FAVORITES_LOADING', false)
      }
    },

    async addFavorite({ state, commit }, analysisId) {
      try {
        await favoritesAPI.addFavorite(state.uuid, analysisId)
        commit('ADD_FAVORITE_ID', analysisId)
      } catch (e) {
        console.error('Failed to add favorite', e)
        throw e
      }
    },

    async removeFavorite({ state, commit }, analysisId) {
      try {
        await favoritesAPI.removeFavorite(state.uuid, analysisId)
        commit('REMOVE_FAVORITE_ID', analysisId)
        commit('REMOVE_MATCH_FROM_LIST', analysisId)
      } catch (e) {
        console.error('Failed to remove favorite', e)
        throw e
      }
    },

    toggleFavoritesMode({ commit, dispatch, state }) {
      const newMode = !state.favoritesMode
      commit('SET_FAVORITES_MODE', newMode)
      if (newMode) {
        commit('SET_POPULAR_MODE', false) // mutually exclusive
        commit('SET_BETS_MODE', false)
        commit('SET_RANKING_MODE', false)
        commit('SET_FOLLOWS_MODE', false)
        commit('SET_FAVORITE_MATCHES', { matches: [], page: 1, hasMore: false })
        dispatch('loadFavoriteMatches', 1)
      }
    },

    async loadPopularMatches({ state, commit }, page = 1) {
      commit('SET_POPULAR_LOADING', true)
      try {
        const res = await favoritesAPI.getPopularFavorites(state.uuid, page, 30)
        commit('SET_POPULAR_MATCHES', {
          matches: res.data.items || [],
          page,
          hasMore: res.data.has_more
        })
      } catch (e) {
        console.error('Failed to load popular favorites', e)
      } finally {
        commit('SET_POPULAR_LOADING', false)
      }
    },

    togglePopularMode({ commit, dispatch, state }) {
      const newMode = !state.popularMode
      commit('SET_POPULAR_MODE', newMode)
      if (newMode) {
        commit('SET_FAVORITES_MODE', false) // mutually exclusive
        commit('SET_BETS_MODE', false)
        commit('SET_RANKING_MODE', false)
        commit('SET_FOLLOWS_MODE', false)
        commit('SET_POPULAR_MATCHES', { matches: [], page: 1, hasMore: false })
        dispatch('loadPopularMatches', 1)
      }
    },

    async loadBetMatches({ state, commit }, page = 1) {
      commit('SET_BETS_LOADING', true)
      try {
        const res = await betAPI.getBets(state.uuid, page, 30)
        const bets = res.data.items || []
        
        // Populate betMatchIds map
        if (page === 1) {
          const idsMap = {}
          bets.forEach(b => {
             if (b.analysis_id) idsMap[b.analysis_id] = true
          })
          commit('SET_BET_MATCH_IDS', idsMap)
        }

        commit('SET_BETS_DATA', {
          matches: bets,
          stats: res.data.total_stats,
          page,
          hasMore: res.data.has_more
        })
      } catch (e) {
        console.error('Failed to load bets', e)
      } finally {
        commit('SET_BETS_LOADING', false)
      }
    },

    toggleBetsMode({ commit, dispatch, state }) {
      const newMode = !state.betsMode
      commit('SET_BETS_MODE', newMode)
      if (newMode) {
        commit('SET_FAVORITES_MODE', false)
        commit('SET_POPULAR_MODE', false)
        commit('SET_RANKING_MODE', false)
        commit('SET_FOLLOWS_MODE', false)
        commit('SET_BETS_DATA', { matches: [], stats: null, page: 1, hasMore: false })
        dispatch('loadBetMatches', 1)
      }
    },

    async loadRanking({ state, commit }) {
      commit('SET_RANKING_LOADING', true)
      try {
        const res = await rankingAPI.getRanking(state.uuid)
        commit('SET_RANKING_DATA', res.data.users || [])
      } catch (e) {
        console.error('Failed to load ranking', e)
      } finally {
        commit('SET_RANKING_LOADING', false)
      }
    },
    toggleRankingMode({ commit, dispatch, state }) {
      const newMode = !state.rankingMode
      commit('SET_RANKING_MODE', newMode)
      if (newMode) {
        commit('SET_FAVORITES_MODE', false)
        commit('SET_POPULAR_MODE', false)
        commit('SET_BETS_MODE', false)
        commit('SET_FOLLOWS_MODE', false)
        dispatch('loadRanking')
      }
    },
    async loadFollows({ state, commit }) {
      commit('SET_FOLLOWS_LOADING', true)
      try {
        const res = await followsAPI.getFollows(state.uuid)
        commit('SET_FOLLOWS_DATA', res.data.users || [])
      } catch (e) {
        console.error('Failed to load follows', e)
      } finally {
        commit('SET_FOLLOWS_LOADING', false)
      }
    },
    toggleFollowsMode({ commit, dispatch, state }) {
      const newMode = !state.followsMode
      commit('SET_FOLLOWS_MODE', newMode)
      if (newMode) {
        commit('SET_FAVORITES_MODE', false)
        commit('SET_POPULAR_MODE', false)
        commit('SET_BETS_MODE', false)
        commit('SET_RANKING_MODE', false)
        dispatch('loadFollows')
      }
    },
    async toggleFollowUser({ state }, { followedUserId, isFollowing }) {
      try {
        await followsAPI.toggleFollow(state.uuid, followedUserId, isFollowing)
      } catch (e) {
        console.error('Failed to toggle follow user', e)
        throw e
      }
    }
  }
})
